import {
  Card,
  Grid,
  H5,
  H6,
  P,
  Stack,
  Button,
  Inline
} from "@adfenix/alexandria";
import { ProgramService, TemplateService } from "@adfenix/types";
import { channelToPascalCase, subjectAttributeToPascalCase } from "utils/types";
import { ToggleButton } from "../meta/template/ToggleButton";
import { useState } from "react";
import { getSquaredIcon } from "utils/channelIcons";
import { useCustomTemplateContext } from "../util/CampaignTemplateContext";
import { useFormContext } from "react-hook-form";
import { useGetImageTemplates } from "api/queries/templateService";
import { styled } from "styled-components";

interface Props {
  hqId: string;
}

export const ImageTemplates = ({ hqId }: Props) => {
  const { setValue, watch } = useFormContext<ProgramService.ProgramModel>();
  const subjectType = watch("subjectType");
  const { data: imageTemplates } = useGetImageTemplates(hqId, data =>
    data
      .filter(template => !template.channels.includes("displayAd"))
      .filter(
        template =>
          subjectAttributeToPascalCase(template.subject) === subjectType
      )
  );

  const {
    campaignTemplate: { previewImageTemplateId },
    campaignKey
  } = useCustomTemplateContext();

  const [previewMode, setPreviewMode] = useState<"list" | "single">(
    previewImageTemplateId ? "single" : "list"
  );

  const [toggled, setToggled] = useState(!!previewImageTemplateId);

  return (
    <Stack spacing="s">
      <H5>Image template</H5>

      {!previewImageTemplateId && (
        <ToggleButton
          isToggled={toggled}
          setToggle={setToggled}
          toggleMessage="Add image template"
          toggledMessage="Cancel"
        />
      )}
      {toggled && previewMode === "single" && (
        <ImageTemplateList
          mode="Switch"
          imageTemplates={
            imageTemplates?.filter(x => x.id === previewImageTemplateId) ?? []
          }
          onClick={() => {
            setPreviewMode("list");
          }}
        />
      )}

      {toggled && previewMode === "list" && (
        <ImageTemplateList
          mode="Add"
          imageTemplates={imageTemplates ?? []}
          onClick={id => {
            setValue(`${campaignKey}.previewImageTemplateId`, id, {
              shouldDirty: true
            });
            setPreviewMode("single");
          }}
        />
      )}
    </Stack>
  );
};

const StyledH6 = styled(H6)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImageTemplateList = ({
  imageTemplates,
  onClick,
  mode
}: {
  imageTemplates: TemplateService.ImageTemplateModel[];
  onClick: (id: string) => void;
  mode: "Switch" | "Add";
}) => {
  return (
    <Grid columns="repeat(auto-fill, minmax(200px, 1fr))" gap="m">
      {imageTemplates?.map(template => (
        <Card p="s" key={template.id}>
          <StyledH6>{template.name}</StyledH6>
          <Stack spacing="xs">
            <P color="grey300" size="xs">
              Cloudinary
            </P>
            <Card p="xs2" justifyContent="center" display="flex">
              <img src={template.previewUrl} style={{ height: "166px" }} />
            </Card>

            <Inline gap="xs2">
              {template.channels.map(x =>
                getSquaredIcon(channelToPascalCase(x))
              )}
            </Inline>

            <Button
              logLabel="add-image-to-custom-template"
              variant={mode === "Add" ? "primary" : "secondary"}
              style={{ width: "100%" }}
              onClick={() => onClick(template.id)}
            >
              {mode}
            </Button>
          </Stack>
        </Card>
      ))}
    </Grid>
  );
};
